import React, { createContext, useContext } from 'react';

interface NavigationContextType {
  navigate: (path: string) => void;
  params: Record<string, string>;
}

const NavigationContext = createContext<NavigationContextType | null>(null);

export const NavigationProvider: React.FC<{ children: React.ReactNode; params?: Record<string, string> }> = ({ 
  children, 
  params = {} 
}) => {
  const navigate = (path: string) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  return (
    <NavigationContext.Provider value={{ navigate, params }}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigate = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    // Fallback for when not in context
    return (path: string) => {
      window.history.pushState({}, '', path);
      window.dispatchEvent(new PopStateEvent('popstate'));
    };
  }
  return context.navigate;
};

export const useParams = <T extends Record<string, string>>(): T => {
  const context = useContext(NavigationContext);
  if (!context) {
    // Parse params from URL as fallback
    const path = window.location.pathname;
    const match = path.match(/\/admin\/restaurants\/(\d+)$/);
    if (match) {
      return { id: match[1] } as unknown as T;
    }
    return {} as T;
  }
  return context.params as T;
};

export const useLocation = () => {
  return {
    pathname: typeof window !== 'undefined' ? window.location.pathname : '/'
  };
};

// Export named exports that match react-router-dom
export const Link: React.FC<{ to: string; children: React.ReactNode; className?: string }> = ({ 
  to, 
  children, 
  className 
}) => {
  const navigate = useNavigate();
  
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(to);
  };

  return (
    <a href={to} onClick={handleClick} className={className}>
      {children}
    </a>
  );
};